// ACTIVE TRADES — every trade the trader is running, Futures and Options in one place, scoped by
// Product and Environment (the same scope as the Trading Book). Monitoring only: a row opens the
// trade where it is managed (the Futures trade panel or the Options Hedging trade).
import React, { useCallback, useEffect, useState } from "react";
import RequestStatus from "../components/RequestStatus";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr, inrPnl, pnlColor } from "@/lib/format";
import { modeLabel, modeTitle } from "@/lib/modes";
import { pnlHeadline, progressText } from "@/lib/marks";
import { Activity } from "lucide-react";
import BookScope, { useBookScope } from "@/components/BookScope";
import { futuresTradeState, toneClass } from "@/lib/tradeState";
import { tradeStatusText, STATUS_TONE } from "@/lib/optionsText";
import { sharedGet, visibleInterval } from "@/lib/poll";

const envOf = (t) => t.environment || t.trading_environment || "PAPER";
const envLabel = (e) => modeLabel(e);

function FuturesTable({ trades, nav }) {
  return (
    <div className="terminal-table-scroll rounded-sm border border-[#2A2F3A]" tabIndex={0} role="region" aria-label="Futures trades — scroll horizontally for all columns" data-testid="active-trades-table">
      <table className="w-full text-[11px] mono border-collapse">
        <thead className="bg-[#1A1D24] text-slate-400"><tr>
          {["Trade", "Market", "Underlying", "SELL (exact)", "BUY (exact)", "State", "Open Lvls", "Level Progress", "Qty/Lot",
            "Allocated", "Funds Used", "Unrealised P&L", "Realised P&L", "Started"].map((h) => (
            <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {trades.length === 0 && <tr><td colSpan={14} className="px-3 py-4 text-center text-slate-600">No Futures trades.</td></tr>}
          {trades.map((t) => (
            <tr key={t.id} className="gz-row cursor-pointer"
              onClick={() => nav(`/paper?open=${t.id}`)} data-testid={`active-trade-row-${t.slot}`}>
              <td className="px-2 py-1.5 font-bold text-fg"><button className="btn-x text-left hover:underline" onClick={(event) => { event.stopPropagation(); nav(`/paper?open=${t.id}`); }}>{t.name}</button><div className="text-[10px] text-slate-400">{envLabel(envOf(t))}</div></td>
              <td className="px-2 py-1.5">
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${t.market_segment === "NFO" ? "bg-[#5b9bff]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
                  {t.market || (t.market_segment === "NFO" ? "NFO FUT" : "MCX FUT")}
                </span>
              </td>
              <td className="px-2 py-1.5 text-slate-200">{t.assigned_commodity || "—"}</td>
              <td className="px-2 py-1.5 text-[#FF6b62] text-[10px]">{t.sell_symbol || "—"}</td>
              <td className="px-2 py-1.5 text-[#5b9bff] text-[10px]">{t.buy_symbol || "—"}</td>
              <td className="px-2 py-1.5">{(() => { const st = futuresTradeState(t); return (
                <span title={st.title} data-testid={`trade-state-${t.id}`}
                  className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold whitespace-nowrap ${toneClass(st.tone)}`}>{st.label}</span>); })()}</td>
              <td className="px-2 py-1.5">{t.open_levels}</td>
              <td className="px-2 py-1.5">{t.pair_execution_enabled ? `${t.completed_full_levels ?? 0} complete${t.current_partial_level ? ` + L${t.current_partial_level} partial` : ""}` : t.entered_levels}</td>
              <td className="px-2 py-1.5">{t.qty_per_lot || "—"}</td>
              <td className="px-2 py-1.5">{inr(t.paper_allocated_capital ?? t.allocated_funds)}</td>
              <td className="px-2 py-1.5">{inr(t.paper_capital_used ?? t.funds_used)}</td>
              <td className={`px-2 py-1.5 ${pnlColor(t.unrealised_pnl)}`}>{inrPnl(t.unrealised_pnl, 0)}</td>
              <td className={`px-2 py-1.5 ${pnlColor(t.realised_pnl)}`}>{inrPnl(t.realised_pnl, 0)}</td>
              <td className="px-2 py-1.5 text-slate-500">{(t.updated_at || "").slice(0, 19).replace("T", " ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OptionsTable({ trades, nav }) {
  return (
    <div className="terminal-table-scroll rounded-sm border border-[#2A2F3A]" tabIndex={0} role="region" aria-label="Options trades" data-testid="active-options-table">
      <table className="w-full text-[11px] mono border-collapse">
        <thead className="bg-[#1A1D24] text-slate-400"><tr>
          {["Trade", "Market", "Underlying", "Contracts (CE + PE)", "Expiry", "State", "Pairs", "Allot Funds", "Unrealised P&L", "Realised P&L"].map((h) => (
            <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {trades.length === 0 && <tr><td colSpan={10} className="px-3 py-4 text-center text-slate-600">No active Options trades.</td></tr>}
          {trades.map((t) => (
            <tr key={t.id} className="gz-row cursor-pointer" onClick={() => nav(t.open_path)} data-testid={`active-option-row-${t.id}`}>
              <td className="px-2 py-1.5 font-bold text-fg" title={t.display_name && t.display_name !== t.name ? `Created as ${t.name}` : undefined}>
                <span data-testid={`option-name-${t.id}`}>{t.display_name || t.name}</span>
                <div className="text-[10px] mono font-normal text-slate-400" title={t.id} data-testid={`option-id-${t.id}`}>#{String(t.id).slice(0, 8)}</div>
                <div className="text-[10px] text-slate-400" title={modeTitle(t.environment)}>{envLabel(t.environment)}</div></td>
              <td className="px-2 py-1.5"><span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold bg-[#5b9bff]/15 text-[#5b9bff]">{t.market}</span></td>
              <td className="px-2 py-1.5 text-slate-200">{t.underlying}</td>
              <td className="px-2 py-1.5 text-[10px] text-[#5b9bff]">{(t.contracts || []).join(" + ")}</td>
              <td className="px-2 py-1.5">{t.expiry}</td>
              <td className="px-2 py-1.5">
                <span title={t.status} data-testid={`trade-state-${t.id}`}
                  className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold whitespace-nowrap ${toneClass({ good: "good", busy: "busy", bad: "bad", done: "done", idle: "idle" }[STATUS_TONE(t)])}`}>
                  {tradeStatusText(t)}</span>
                {t.needs_attention && <span className="ml-1 text-[9px] px-1 py-0.5 rounded-sm font-bold bg-[#FF3B30]/20 text-[#FF6b62]" data-testid="needs-attention">NEEDS ATTENTION</span>}
              </td>
              <td className="px-2 py-1.5">{t.entered_pairs}{t.planned_pairs ? ` / ${t.planned_pairs}` : ""}</td>
              <td className="px-2 py-1.5">{inr(t.allocated)}</td>
              <td className={`px-2 py-1.5 ${t.price_state && t.price_state !== "LIVE" ? "text-slate-500" : pnlColor(t.unrealised_pnl)}`} data-testid={`option-unrealised-${t.id}`}
                title="Net, if every open pair were sold at the current bids (after entry and estimated exit charges)">
                {t.no_position ? <span className="text-slate-500 text-[11px]" data-testid={`option-no-position-${t.id}`}
                  title="Nothing has been bought or sold yet: the trade is waiting for its entry conditions">No position yet</span>
                  : t.price_state === "MARKET_CLOSED" && t.unrealised_mark != null
                    ? <span title={`Last-price mark. Closing now at the bid / ask: ${inrPnl(t.unrealised_pnl, 0)}`} data-testid={`option-mark-${t.id}`}>{inrPnl(t.unrealised_mark, 0)}
                      <div className="text-[9px] text-slate-500 font-normal">close now {inrPnl(t.unrealised_pnl, 0)}</div></span>
                    : inrPnl(t.unrealised_pnl, 0)}
                {!t.no_position && t.price_state && t.price_state !== "LIVE" && t.unrealised_pnl != null &&
                  <div className="text-[9px] font-bold text-[#FF6b62]" data-testid={`option-price-state-${t.id}`}>{t.price_state === "MARKET_CLOSED" ? "MARKET CLOSED" : t.price_state === "STALE" ? "STALE PRICE" : t.price_state.replace("_", " ")}</div>}
                {t.valuation && <div className="text-[9px] font-bold text-[#B45309]" data-testid={`option-valuation-${t.id}`}
                  title="The open short quantity exceeds the visible asks: the unseen part is valued at the worst visible ask. A profit target is not taken on it.">{t.valuation}</div>}
              </td>
              <td className={`px-2 py-1.5 ${pnlColor(t.realised_pnl)}`}>{inrPnl(t.realised_pnl, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ActiveTrades() {
  const scope = useBookScope();
  const [futures, setFutures] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  const nav = useNavigate();
  const showF = scope.product !== "OPTIONS" && scope.products.futures;
  const showO = scope.product !== "FUTURES" && scope.products.options;

  const load = useCallback(() => {
    const env = scope.environment === "ALL" ? "ALL" : scope.environment;
    const jobs = [];
    if (showF) {
      jobs.push(sharedGet("/paper/v25/trades", { params: { market: "ALL", environment: "ALL" } })
        .then((r) => setFutures((r.data.trades || []).filter((t) => env === "ALL" || envOf(t) === env))));
    }
    if (showO) {
      jobs.push(api.get("/paper-book/active-trades", { params: { product: "OPTIONS", environment: env } })
        .then((r) => setOptions(r.data.options || [])));
    }
    Promise.all(jobs).then(() => setError(null))
      .catch(() => setError("Trade updates are unavailable. Any displayed values are from the last successful update."));
  }, [showF, showO, scope.environment]);
  useEffect(() => {
    load(); return visibleInterval(load, 4000);
  }, [load]);

  const loading = (showF && futures == null) || (showO && options == null);
  return (
    <div className="p-4 space-y-3">
      <RequestStatus error={error} onRetry={load} />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><Activity className="h-5 w-5 text-[#2563EB]" /> Active Trades
          <span className="text-xs font-normal ml-2 text-slate-500">every trade you are running · click a row to manage it</span>
        </h1>
        <BookScope scope={scope} />
      </div>
      {loading ? <RequestStatus loading="Loading active trades…" onRetry={load} /> : <>
        {showF && <section className="space-y-1.5" data-testid="active-futures">
          {showO && <h2 className="text-xs font-bold text-slate-400">FUTURES</h2>}
          <FuturesTable trades={futures || []} nav={nav} />
        </section>}
        {showO && <section className="space-y-1.5" data-testid="active-options">
          {showF && <h2 className="text-xs font-bold text-slate-400">OPTIONS</h2>}
          <OptionsTable trades={options || []} nav={nav} />
        </section>}
      </>}
      <div className="text-[10px] text-slate-600">Monitoring view. Futures trades open in the Trade Panel, Options trades in Hedging. Positions, orders, fills and P&L for all of them are in the Trading Book.</div>
    </div>
  );
}
